import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';

/**
 * @typedef {Object} SubathonTimerSettings
 * @property {boolean} negativeTimeAllowed
 * @property {number} endTimeOffset
 * @property {string} campaignID
 */

const settingsBaseURL = import.meta.env.VITE_SUBATHON_TIMER_SERVICE_URI;

/**
 *
 * @param {string} url
 * @param {'GET' | 'PATCH' | 'DELETE'} method
 * @param {any|null} body
 * @returns {Promise<any>}
 */
const fetchJSON = async (url, method = 'GET', body) => {
  return fetch(url, {
    method: method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then((res) => res.json());
};


/**
 *
 * @param campaignID
 * @returns {{settings: {campaignID, endTimeOffset: number, negativeTimeAllowed: boolean}, updateSettings: ((function(*, *): void)|*), deleteSettings: ((function(*): void)|*), loading: boolean}}
 */
export function useSubathonTimerSettings(campaignID) {
  const campaignRef = useRef(campaignID);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState({
    campaignID,
    endTimeOffset: 5,
    negativeTimeAllowed: false,
  });

  const stopLoading = useCallback(() => {
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const defaultSettings = useMemo(() => {
    return {
      campaignID,
      endTimeOffset: 5,
      negativeTimeAllowed: false,
    };
  }, [campaignID]);

  const updateSettings = useCallback(
    (settings, didUpdate) => {
      if (!campaignID || !campaignID.length) return;
      const settingsURL = `${settingsBaseURL}/settings/${campaignID}`;

      fetchJSON(settingsURL, 'PATCH', settings).then((res) => {
        if (res) setSettings(res);
        if (didUpdate) didUpdate();
      });
    },
    [campaignID]
  );

  const deleteSettings = useCallback(
    (didDelete) => {
      if (!campaignID || !campaignID.length) return;
      const settingsURL = `${settingsBaseURL}/settings/${campaignID}`;

      fetchJSON(settingsURL, 'DELETE').then((res) => {
        if (res) setSettings(defaultSettings);
        if (didDelete) didDelete();
      });
    },
    [campaignID, defaultSettings]
  );

  const getSettings = useCallback(() => {
    if (!campaignID || !campaignID.length) return;

    const settingsURL = `${settingsBaseURL}/settings/${campaignID}`;

    fetchJSON(settingsURL, 'GET').then((res) => {
      if (res) {
        setSettings(res);
        stopLoading();
      }
    });
  }, [campaignID, stopLoading]);

  const { data: settingsForCampaign } = useQuery({
    queryKey: ['getSettingsForCampaign', campaignID],
    queryFn: async () => {
      if (!campaignID || !campaignID.length) return null;
      const settingsURL = `${settingsBaseURL}/settings/${campaignID}`;
      return await fetchJSON(settingsURL, 'GET');
    },
    staleTime: 5000,
  });

  useEffect(() => {
    setSettings(settingsForCampaign ?? defaultSettings);
    stopLoading();
  }, [settingsForCampaign, defaultSettings, stopLoading]);

  useEffect(() => {
    if (campaignRef.current !== campaignID) {
      setIsLoading(true);
      getSettings();
    }
  }, [campaignID, getSettings]);

  return {
    settings,
    updateSettings,
    deleteSettings,
    loading: isLoading,
  };
}

/**
 *
 * @param {SubathonTimerSettings|null} settings
 */
export const useSubathonTimerSettingsModifiers = (settings) => {
  const initialSettings = useRef(settings);
  const [contentChanged, setContentChanged] = useState(false);
  const isNew = useMemo(() => settings === null, [settings]);
  const [negativeTimeAllowed, setNegativeTimeAllowed] = useState(
    settings?.negativeTimeAllowed ?? false
  );
  const [endTimeOffset, setEndTimeOffset] = useState(
    settings?.endTimeOffset ?? 5
  );

  useEffect(() => {
    if (settings && initialSettings.current !== settings) {
      initialSettings.current = settings;
      setNegativeTimeAllowed(settings.negativeTimeAllowed);
      setEndTimeOffset(settings.endTimeOffset);
      setContentChanged(false);
    }
  }, [settings]);

  const differsFromOriginal = useCallback((key, value) => {
    return initialSettings.current[key] !== value;
  }, []);

  const handleNegativeTimeAllowedChange = useCallback(
    (event) => {
      const newValue = event.target.value === 'yes';
      if (newValue !== negativeTimeAllowed) {
        setContentChanged(differsFromOriginal('negativeTimeAllowed', newValue));
        setNegativeTimeAllowed(newValue);
      }
    },
    [differsFromOriginal, negativeTimeAllowed]
  );

  const handleEndTimeOffsetChange = useCallback(
    (event) => {
      const newValue = event.target.value;
      if (newValue !== endTimeOffset) {
        setContentChanged(differsFromOriginal('endTimeOffset', newValue));
        setEndTimeOffset(newValue);
      }
    },
    [differsFromOriginal, endTimeOffset]
  );

  return {
    isNew,
    contentChanged,
    negativeTimeAllowed,
    endTimeOffset,
    handleEndTimeOffsetChange,
    handleNegativeTimeAllowedChange,
  };
};
